import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSpinner, faCheckCircle, faExclamationTriangle,
    faImage, faSave, faTrashAlt
} from '@fortawesome/free-solid-svg-icons';

import { familyService } from '../services';
import { validateFamilyForm } from '../services/validation';

import './FormPage.css';

// Helper functions for dropdowns
const getMonths = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
        months.push({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) });
    }
    return months;
};

const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && parseInt(year, 10) >= 1800) ? parseInt(year, 10) : 2000;
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};

const getYears = () => {
    const years = [{ value: '', label: 'Year (Optional)' }];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 100; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};


function EditFamilyPage() {
    const { familyId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // Form State
    const [surname, setSurname] = useState('');
    const [selectedAnniversaryMonth, setSelectedAnniversaryMonth] = useState('');
    const [selectedAnniversaryDay, setSelectedAnniversaryDay] = useState('');
    const [selectedAnniversaryYear, setSelectedAnniversaryYear] = useState('');
    const [phone, setPhone] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipcode, setZipcode] = useState('');

    // Image State
    const [imageFile, setImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [existingPhotoPath, setExistingPhotoPath] = useState(null);
    const [isPhotoRemoved, setIsPhotoRemoved] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Component State
    const [isLoading, setIsLoading] = useState(false);
    const [loadingInitialData, setLoadingInitialData] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isReady, setIsReady] = useState(false);

    // Fetch family data
    const fetchFamilyData = useCallback(async () => {
        if (!familyId) {
            setMessage({ type: 'danger', text: 'Error: Family ID not provided.' });
            setLoadingInitialData(false);
            return;
        }

        setLoadingInitialData(true);
        setMessage({ type: '', text: '' });

        try {
            const family = await familyService.getFamily(familyId);

            if (!family) {
                setMessage({ type: 'danger', text: 'Family not found or you do not have permission to edit it.' });
                setLoadingInitialData(false);
                return;
            }

            // Populate form fields
            setSurname(family.surname || '');
            setPhone(family.phone || '');
            setStreet(family.street || '');
            setCity(family.city || '');
            setState(family.state || '');
            setZipcode(family.zipcode || '');

            // Handle anniversary fields
            let initialMonth = family.anniversaryMonth ? String(family.anniversaryMonth).trim() : '';
            let initialDay = family.anniversaryDay ? String(family.anniversaryDay).trim() : '';
            let initialYear = family.anniversaryYear ? String(family.anniversaryYear).trim() : '';

            if (!initialMonth || !initialDay) {
                initialYear = '';
            }

            setSelectedAnniversaryMonth(initialMonth);
            setSelectedAnniversaryDay(initialDay);
            setSelectedAnniversaryYear(initialYear);

            // Handle photo
            setExistingPhotoPath(family.photo || null);

            if (family.photo) {
                try {
                    const url = await familyService.getFamilyPhotoUrl(family.photo, family.imageUpdatedAt);
                    setImagePreviewUrl(url);
                } catch (urlError) {
                    setImagePreviewUrl(null);
                }
            } else {
                setImagePreviewUrl(null);
            }

            setIsReady(true);
        } catch (error) {
            setMessage({ type: 'danger', text: `Error loading family data: ${error.message}` });
        } finally {
            setLoadingInitialData(false);
        }
    }, [familyId]);

    useEffect(() => {
        fetchFamilyData();
    }, [fetchFamilyData]);

    // Cleanup image preview URL
    useEffect(() => {
        return () => {
            if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
        };
    }, [imagePreviewUrl]);

    const handleFileChange = (event) => {
        setMessage({ type: '', text: '' });
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setImageFile(file);
            setIsPhotoRemoved(false);

            const newPreviewUrl = URL.createObjectURL(file);
            if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
            setImagePreviewUrl(newPreviewUrl);
        } else {
            if (file) {
                setMessage({ type: 'warning', text: 'Please select a valid image file.' });
            }
        }
        if (event.target) event.target.value = null;
    };

    const handleRemovePhoto = () => {
        setMessage({ type: '', text: '' });
        setImageFile(null);
        if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreviewUrl);
        }
        setImagePreviewUrl(null);
        setIsPhotoRemoved(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage({ type: '', text: '' });

        const formData = {
            surname,
            phone,
            state,
            zipcode,
            anniversaryMonth: selectedAnniversaryMonth,
            anniversaryDay: selectedAnniversaryDay,
            anniversaryYear: selectedAnniversaryYear,
        };

        const validation = validateFamilyForm(formData);
        if (!validation.isValid) {
            const firstError = Object.values(validation.errors)[0];
            setMessage({ type: 'warning', text: firstError });
            return;
        }

        if (!familyId) {
            setMessage({ type: 'danger', text: 'Cannot update family: Missing family ID.' });
            return;
        }

        setIsLoading(true);
        setUploadProgress(0);

        try {
            setMessage({ type: 'info', text: imageFile ? 'Uploading new photo...' : 'Updating family...' });

            let photoFile = undefined;
            if (imageFile) {
                photoFile = imageFile;
            } else if (isPhotoRemoved) {
                photoFile = null;
            }

            const result = await familyService.updateFamily({
                familyId,
                surname: surname.trim(),
                street: street.trim() || null,
                city: city.trim() || null,
                state: state.trim().toUpperCase() || null,
                zipcode: zipcode.trim() || null,
                phone: phone.trim() || null,
                anniversaryMonth: selectedAnniversaryMonth || null,
                anniversaryDay: selectedAnniversaryDay || null,
                anniversaryYear: selectedAnniversaryYear || null,
                photoFile,
            }, {
                onUploadProgress: (progress) => {
                    setUploadProgress(progress);
                    if (progress === 100) {
                        setMessage({ type: 'info', text: 'Photo uploaded. Updating family...' });
                    }
                },
            });

            if (result.success) {
                setMessage({ type: 'success', text: 'Family updated successfully!' });

                if (result.photoPath !== undefined) {
                    setExistingPhotoPath(result.photoPath);
                    if (result.photoPath) {
                        if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
                            URL.revokeObjectURL(imagePreviewUrl);
                        }
                        try {
                            const finalUrl = await familyService.getFamilyPhotoUrl(result.photoPath, result.imageUpdatedAt);
                            setImagePreviewUrl(finalUrl);
                        } catch (e) {
                            setImagePreviewUrl(null);
                        }
                    } else {
                        setImagePreviewUrl(null);
                    }
                }

                const hadImageFile = !!imageFile;
                setImageFile(null);
                setIsPhotoRemoved(false);

                if (hadImageFile) {
                    setTimeout(() => navigate(`/families/${familyId}/assign-photos/all`, { state: { fromPhotoUpload: true } }), 1500);
                } else {
                    setTimeout(() => navigate(`/families/${familyId}`), 1500);
                }
            } else {
                throw new Error(result.error || 'Failed to update family.');
            }
        } catch (error) {
            setMessage({ type: 'danger', text: error.message || 'An unexpected error occurred.' });
        } finally {
            setIsLoading(false);
        }
    };

    const availableMonths = React.useMemo(() => getMonths(), []);
    const availableDays = React.useMemo(() => getDaysInMonth(selectedAnniversaryMonth, selectedAnniversaryYear), [selectedAnniversaryMonth, selectedAnniversaryYear]);
    const availableYears = React.useMemo(() => getYears(), []);

    // Loading state
    if (loadingInitialData) {
        return (
            <div className="form-page">
                <div className="form-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-form"></div>
                </div>
            </div>
        );
    }

    // Error state - can't load family
    if (message.type === 'danger' && !isReady) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/families" className="form-back-btn" title="Back to Families">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Edit Family</h1>
                                    <p className="form-page-subtitle">Error loading family</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-card">
                        <div className="form-card-body">
                            <div className="form-alert form-alert-danger">
                                <div className="form-alert-icon">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                </div>
                                <div className="form-alert-content">
                                    {message.text || 'Failed to load family data.'}
                                </div>
                            </div>
                            <Link to="/families" className="form-submit-btn" style={{ textDecoration: 'none' }}>
                                Back to Families
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="form-page">
            {/* Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to={`/families/${familyId}`} className="form-back-btn" title="Back to Family">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">Edit Family</h1>
                                <p className="form-page-subtitle">{surname ? `${surname} Family` : 'Update family details'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="form-card">
                    <div className="form-card-body">
                        <form onSubmit={handleSubmit}>
                            {/* Alert Message */}
                            {message.text && (
                                <div className={`form-alert form-alert-${message.type || 'info'}`}>
                                    <div className="form-alert-icon">
                                        {message.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} />}
                                        {(message.type === 'danger' || message.type === 'warning') && <FontAwesomeIcon icon={faExclamationTriangle} />}
                                        {message.type === 'info' && <FontAwesomeIcon icon={faSpinner} spin />}
                                    </div>
                                    <div className="form-alert-content">
                                        {message.text}
                                        {isLoading && uploadProgress > 0 && imageFile && (
                                            <div className="form-progress">
                                                <div className="form-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Basic Info Section */}
                            <div className="form-section">
                                <div className="form-section-title">Basic Information</div>

                                <div className="form-field">
                                    <label className="form-field-label" htmlFor="surname">
                                        Family Surname <span className="required">*</span>
                                    </label>
                                    <input
                                        id="surname"
                                        type="text"
                                        className="form-field-input"
                                        value={surname}
                                        onChange={(e) => setSurname(e.target.value)}
                                        placeholder="Enter family surname"
                                        disabled={isLoading}
                                        required
                                    />
                                </div>

                                <div className="form-field">
                                    <label className="form-field-label" htmlFor="phone">Home Phone</label>
                                    <input
                                        id="phone"
                                        type="tel"
                                        className="form-field-input"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="(Optional)"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            {/* Address Section */}
                            <div className="form-section">
                                <div className="form-section-title">Address</div>

                                <div className="form-field">
                                    <label className="form-field-label" htmlFor="street">Street Address</label>
                                    <input
                                        id="street"
                                        type="text"
                                        className="form-field-input"
                                        value={street}
                                        onChange={(e) => setStreet(e.target.value)}
                                        placeholder="(Optional)"
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className="form-field-row form-field-row-3">
                                    <div className="form-field">
                                        <label className="form-field-label" htmlFor="city">City</label>
                                        <input
                                            id="city"
                                            type="text"
                                            className="form-field-input"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            placeholder="City"
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label" htmlFor="state">State</label>
                                        <input
                                            id="state"
                                            type="text"
                                            className="form-field-input"
                                            value={state}
                                            onChange={(e) => setState(e.target.value)}
                                            placeholder="NC"
                                            maxLength="2"
                                            style={{ textTransform: 'uppercase' }}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label" htmlFor="zipcode">Zip Code</label>
                                        <input
                                            id="zipcode"
                                            type="text"
                                            className="form-field-input"
                                            value={zipcode}
                                            onChange={(e) => setZipcode(e.target.value)}
                                            placeholder="12345"
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Anniversary Section */}
                            <div className="form-section">
                                <div className="form-section-title">Anniversary (Optional)</div>

                                <div className="form-field-row form-field-row-date">
                                    <div className="form-field">
                                        <label className="form-field-label" htmlFor="anniversaryMonthEdit">Month</label>
                                        <select
                                            id="anniversaryMonthEdit"
                                            className="form-field-select"
                                            value={selectedAnniversaryMonth}
                                            onChange={(e) => {
                                                setSelectedAnniversaryMonth(e.target.value);
                                                if (!e.target.value) setSelectedAnniversaryDay('');
                                            }}
                                            disabled={isLoading}
                                        >
                                            <option value="">Select Month</option>
                                            {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label" htmlFor="anniversaryDayEdit">Day</label>
                                        <select
                                            id="anniversaryDayEdit"
                                            className="form-field-select"
                                            value={selectedAnniversaryDay}
                                            onChange={(e) => setSelectedAnniversaryDay(e.target.value)}
                                            disabled={isLoading || !selectedAnniversaryMonth}
                                        >
                                            <option value="">Day</option>
                                            {availableDays.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label" htmlFor="anniversaryYearEdit">Year</label>
                                        <select
                                            id="anniversaryYearEdit"
                                            className="form-field-select"
                                            value={selectedAnniversaryYear}
                                            onChange={(e) => setSelectedAnniversaryYear(e.target.value)}
                                            disabled={isLoading}
                                        >
                                            {availableYears.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Photo Section */}
                            <div className="form-section">
                                <div className="form-section-title">Family Photo</div>

                                <div className="form-photo-section">
                                    {imagePreviewUrl && !isPhotoRemoved ? (
                                        <div className="form-photo-preview">
                                            <img src={imagePreviewUrl} alt="Family preview" />
                                        </div>
                                    ) : isPhotoRemoved ? (
                                        <div className="form-photo-status">Photo will be removed on save</div>
                                    ) : !existingPhotoPath ? (
                                        <div className="form-photo-status">No photo currently set</div>
                                    ) : null}

                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        disabled={isLoading}
                                    />

                                    <div className="form-photo-buttons">
                                        <button
                                            type="button"
                                            className="form-photo-btn form-photo-btn-primary"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isLoading}
                                        >
                                            <FontAwesomeIcon icon={faImage} />
                                            {imagePreviewUrl || existingPhotoPath ? 'Change Photo' : 'Choose Photo'}
                                        </button>

                                        {(imagePreviewUrl || existingPhotoPath) && !isPhotoRemoved && (
                                            <button
                                                type="button"
                                                className="form-photo-btn form-photo-btn-danger"
                                                onClick={handleRemovePhoto}
                                                disabled={isLoading}
                                            >
                                                <FontAwesomeIcon icon={faTrashAlt} />
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="form-submit-btn"
                                    disabled={isLoading || !isReady}
                                >
                                    {isLoading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            {uploadProgress > 0 && imageFile ? `Uploading ${uploadProgress}%` : 'Updating...'}
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditFamilyPage;
